import { Link } from "react-router-dom";
import { BrandedSelect } from "@/components/ui/BrandedSelect";
import {
  birthDateError,
  PRIVACY_POLICY_VERSION,
  type SignupConsent,
} from "@shared/signupConsent";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export interface ConsentDraft {
  month: string;
  year: string;
  accepted: boolean;
}
export const EMPTY_CONSENT: ConsentDraft = {
  month: "",
  year: "",
  accepted: false,
};
export function consentPayload(value: ConsentDraft): SignupConsent {
  return {
    birth_month: Number(value.month),
    birth_year: Number(value.year),
    privacy_accepted: value.accepted as true,
    policy_version: PRIVACY_POLICY_VERSION,
  };
}
export function SignupConsentFields({
  value,
  onChange,
  disabled = false,
}: {
  value: ConsentDraft;
  onChange: (value: ConsentDraft) => void;
  disabled?: boolean;
}) {
  const error =
    value.month && /^\d{4}$/.test(value.year)
      ? birthDateError(Number(value.month), Number(value.year))
      : null;
  return (
    <div className="space-y-4">
      <fieldset
        disabled={disabled}
        aria-describedby="birth-help"
        className="space-y-2"
      >
        <legend className="text-sm font-semibold text-ink">
          When were you born?
        </legend>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="signup-birth-month" className="text-xs text-ink-2">
              Birth month
            </label>
            <BrandedSelect
              id="signup-birth-month"
              label="Birth month"
              value={value.month}
              onValueChange={(month) => onChange({ ...value, month })}
              disabled={disabled}
              options={[
                { value: "", label: "Choose month" },
                ...months.map((label, index) => ({
                  value: String(index + 1),
                  label,
                })),
              ]}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="signup-birth-year" className="text-xs text-ink-2">
              Birth year
            </label>
            <input
              id="signup-birth-year"
              name="birthYear"
              autoComplete="bday-year"
              inputMode="numeric"
              type="text"
              pattern="[0-9]{4}"
              maxLength={4}
              placeholder="YYYY"
              value={value.year}
              onChange={(e) =>
                onChange({ ...value, year: e.target.value.replace(/\D/g, "") })
              }
              required
              aria-invalid={!!error}
              aria-describedby={error ? "birth-error birth-help" : "birth-help"}
              className="h-11 w-full rounded-xl border border-line bg-surface px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember"
            />
          </div>
        </div>
        <p id="birth-help" className="text-xs leading-relaxed text-ink-3">
          We use your month and year to check age eligibility. We do not need
          your birth day or display your birth information publicly. Standard
          accounts are for ages 13+ and must meet your country's minimum age.
        </p>
        {error && (
          <p id="birth-error" role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
      </fieldset>
      <div className="flex items-start gap-3 rounded-xl border border-line bg-surface p-3">
        <input
          id="signup-privacy"
          name="privacyAgreement"
          type="checkbox"
          checked={value.accepted}
          onChange={(e) => onChange({ ...value, accepted: e.target.checked })}
          required
          disabled={disabled}
          className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--accent-primary)]"
        />
        <label
          htmlFor="signup-privacy"
          className="min-h-11 cursor-pointer text-sm leading-relaxed text-ink-2"
        >
          I have read and agree to the{" "}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-ink underline underline-offset-2"
          >
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-ink underline underline-offset-2"
          >
            Terms of Service
          </Link>
          .
        </label>
      </div>
    </div>
  );
}
